import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { createProduct, fetchAdminProduct, updateProduct } from "../../api/admin";
import { imageUrl } from "../../api/shop";

const EMPTY_SPECS = {
  storage: "", ram: "", color: "", network: "", sim: "",
  display: "", displaySize: "", resolution: "", refreshRate: "",
  chipset: "", processor: "", cores: "",
  battery: "", chargingSpeed: "",
  rearCamera: "", frontCamera: "",
  os: "",
  connectivity: "",
  bodyMaterial: "", dimensions: "", weight: "", waterResistance: "",
  warranty: "", boxContents: "",
};

const EMPTY_FORM = {
  title: "", brand: "", sku: "", category: "Smartphone", condition: "new",
  description: "", price: "", discountPrice: "", stock: "0",
  isFeatured: false, isActive: true,
  tags: "",
  specs: EMPTY_SPECS,
  extraSpecs: [],
  existingImages: [],
  newImages: [],
};

const SPEC_GROUPS = [
  {
    title: "Memoria y color",
    fields: [
      ["storage", "Almacenamiento", "256GB"],
      ["ram", "RAM", "8GB"],
      ["color", "Color", "Negro"],
      ["network", "Red", "5G"],
      ["sim", "Tarjetas SIM", "Dual SIM"],
    ],
  },
  {
    title: "Pantalla",
    fields: [
      ["display", "Tipo de pantalla", "AMOLED"],
      ["displaySize", "Tamaño", "6.7 pulgadas"],
      ["resolution", "Resolución", "1440 x 3120"],
      ["refreshRate", "Tasa de refresco", "120Hz"],
    ],
  },
  {
    title: "Rendimiento",
    fields: [
      ["chipset", "Chipset", "Snapdragon 8 Gen 3"],
      ["processor", "Procesador", "Octa-core"],
      ["cores", "Núcleos", "8"],
      ["os", "Sistema operativo", "Android 15"],
    ],
  },
  {
    title: "Batería y carga",
    fields: [
      ["battery", "Batería", "5000mAh"],
      // ["chargingSpeed", "Velocidad de carga", "45W"],
    ],
  },
  {
    title: "Cámara",
    fields: [
      ["rearCamera", "Cámara trasera", "50MP + 12MP + 10MP"],
      ["frontCamera", "Cámara frontal", "12MP"],
    ],
  },
  // {
  //   title: "Diseño y conectividad",
  //   fields: [
  //     ["bodyMaterial", "Material", "Aluminio y cristal"],
  //     ["dimensions", "Dimensiones", "162.3 x 79 x 8.6 mm"],
  //     ["weight", "Peso", "232g"],
  //     ["waterResistance", "Resistencia al agua", "IP68"],
  //     ["connectivity", "Conectividad (separar por comas)", "WiFi 6, NFC, Bluetooth 5.3"],
  //   ],
  // },
  // {
  //   title: "Garantía y contenido de la caja",
  //   fields: [
  //     ["warranty", "Garantía", "12 meses"],
  //     ["boxContents", "Contenido de la caja (separar por comas)", "Móvil, Cable USB-C, Herramienta SIM"],
  //   ],
  // },
];

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { token } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    fetchAdminProduct(id, token)
      .then((p) => {
        setForm({
          title: p.title || "",
          brand: p.brand || "",
          sku: p.sku || "",
          category: p.category || "Smartphone",
          condition: p.condition || "new",
          description: p.description || "",
          price: p.price ?? "",
          discountPrice: p.discountPrice ?? "",
          stock: p.stock ?? "0",
          isFeatured: !!p.isFeatured,
          isActive: p.isActive !== false,
          tags: (p.tags || []).join(", "),
          specs: {
            ...EMPTY_SPECS,
            ...p.specs,
            connectivity: (p.specs?.connectivity || []).join(", "),
            boxContents: (p.specs?.boxContents || []).join(", "),
          },
          extraSpecs: p.extraSpecs || [],
          existingImages: p.images || [],
          newImages: [],
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, isEdit, token]);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const changeSpec = (key, value) => setForm((f) => ({ ...f, specs: { ...f.specs, [key]: value } }));

  const addExtraSpec = () => setForm((f) => ({ ...f, extraSpecs: [...f.extraSpecs, { label: "", value: "" }] }));
  const changeExtraSpec = (i, field, value) =>
    setForm((f) => ({
      ...f,
      extraSpecs: f.extraSpecs.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)),
    }));
  const removeExtraSpec = (i) => setForm((f) => ({ ...f, extraSpecs: f.extraSpecs.filter((_, idx) => idx !== i) }));

  const addImages = (fileList) => {
    const files = Array.from(fileList || []);
    setForm((f) => ({ ...f, newImages: [...f.newImages, ...files] }));
  };
  const removeExistingImage = (name) =>
    setForm((f) => ({ ...f, existingImages: f.existingImages.filter((img) => img !== name) }));
  const removeNewImage = (idx) =>
    setForm((f) => ({ ...f, newImages: f.newImages.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.title || !form.brand || !form.sku || !form.price) {
      setError("Título, marca, SKU y precio son obligatorios.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        specs: {
          ...form.specs,
          connectivity: form.specs.connectivity.split(",").map((s) => s.trim()).filter(Boolean),
          boxContents: form.specs.boxContents.split(",").map((s) => s.trim()).filter(Boolean),
        },
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        extraSpecs: form.extraSpecs.filter((row) => row.label.trim() && row.value.trim()),
      };

      if (isEdit) await updateProduct(id, payload, token);
      else await createProduct(payload, token);

      navigate("/admin/productos");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="hero__skeleton" style={{ height: 400 }} />;

  return (
    <>
      <div className="admin__head">
        <div>
          <p className="eyebrow">{isEdit ? "Editar producto" : "Nuevo producto"}</p>
          <h1 style={{ fontSize: "1.9rem" }}>{isEdit ? `${form.brand} ${form.title}` : "Añadir móvil"}</h1>
        </div>
      </div>

      {error && <div className="alert alert--error">{error}</div>}

      <form onSubmit={submit} noValidate>
        <div className="formcard">
          <h3 style={{ marginBottom: 18 }}>Datos básicos</h3>
          <div className="fieldrow">
            <label className="field">
              <span>Marca *</span>
              <input name="brand" value={form.brand} onChange={change} required />
            </label>
            <label className="field">
              <span>Modelo / título *</span>
              <input name="title" value={form.title} onChange={change} placeholder="Galaxy S24 Ultra" required />
            </label>
          </div>
          <div className="fieldrow">
            <label className="field">
              <span>SKU *</span>
              <input name="sku" value={form.sku} onChange={change} placeholder="SM-S928B-256-BLK" required />
            </label>
            <label className="field">
              <span>Categoría</span>
              <input name="category" value={form.category} onChange={change} />
            </label>
          </div>
          <label className="field">
            <span>Condición</span>
            <select name="condition" value={form.condition} onChange={change}>
              <option value="new">Nuevo</option>
              <option value="refurbished">Reacondicionado</option>
              <option value="used">Usado</option>
            </select>
          </label>
          <label className="field">
            <span>Descripción</span>
            <textarea name="description" value={form.description} onChange={change} rows={4} />
          </label>
          <label className="field">
            <span>Etiquetas (separar por comas)</span>
            <input name="tags" value={form.tags} onChange={change} placeholder="oferta, 5g, gama alta" />
          </label>
        </div>

        <div className="formcard">
          <h3 style={{ marginBottom: 18 }}>Precio y stock</h3>
          <div className="fieldrow">
            <label className="field">
              <span>Precio (€) *</span>
              <input type="number" step="0.01" min="0" name="price" value={form.price} onChange={change} required />
            </label>
            <label className="field">
              <span>Precio con descuento (€)</span>
              <input type="number" step="0.01" min="0" name="discountPrice" value={form.discountPrice} onChange={change} />
              <small>Déjalo vacío si no hay oferta.</small>
            </label>
            <label className="field">
              <span>Stock</span>
              <input type="number" min="0" step="1" name="stock" value={form.stock} onChange={change} />
            </label>
          </div>
          <div className="fieldrow">
            <label className="checkfield">
              <input type="checkbox" name="isFeatured" checked={form.isFeatured} onChange={change} />
              <span>Destacado en portada</span>
            </label>
            <label className="checkfield">
              <input type="checkbox" name="isActive" checked={form.isActive} onChange={change} />
              <span>Publicado (visible en la tienda)</span>
            </label>
          </div>
        </div>

        <div className="formcard">
          <h3 style={{ marginBottom: 6 }}>Fotos</h3>
          <p style={{ fontSize: "0.88rem", color: "var(--stone)", marginBottom: 14 }}>
            La primera foto es la que se usa como portada. Hasta 10 imágenes.
          </p>

          <div className="imagegrid">
            {form.existingImages.map((name) => (
              <div className="imagegrid__item" key={name}>
                <img src={imageUrl(name)} alt="" />
                <button type="button" onClick={() => removeExistingImage(name)} aria-label="Quitar imagen">
                  ×
                </button>
              </div>
            ))}
            {form.newImages.map((file, i) => (
              <div className="imagegrid__item" key={`new-${i}`}>
                <img src={URL.createObjectURL(file)} alt="" />
                <button type="button" onClick={() => removeNewImage(i)} aria-label="Quitar imagen">
                  ×
                </button>
              </div>
            ))}

            <label className="imagegrid__add">
              <input type="file" accept="image/*" multiple hidden onChange={(e) => addImages(e.target.files)} />
              + Añadir
            </label>
          </div>
        </div>

        {SPEC_GROUPS.map((group) => (
          <div className="formcard" key={group.title}>
            <h3 style={{ marginBottom: 18 }}>{group.title}</h3>
            <div className="specgrid">
              {group.fields.map(([key, label, placeholder]) => (
                <label className="field" key={key}>
                  <span>{label}</span>
                  <input value={form.specs[key]} onChange={(e) => changeSpec(key, e.target.value)} placeholder={placeholder} />
                </label>
              ))}
            </div>
          </div>
        ))}

        <div className="formcard">
          <h3 style={{ marginBottom: 6 }}>Especificaciones adicionales</h3>
          <p style={{ fontSize: "0.88rem", color: "var(--stone)", marginBottom: 14 }}>
            Añade cualquier otro detalle que no encaje arriba — banda 5G concreta, certificaciones, accesorios incluidos…
          </p>

          {form.extraSpecs.map((row, i) => (
            <div className="fieldrow" key={i} style={{ alignItems: "flex-end" }}>
              <label className="field">
                <span>Nombre</span>
                <input value={row.label} onChange={(e) => changeExtraSpec(i, "label", e.target.value)} placeholder="Certificación" />
              </label>
              <label className="field">
                <span>Valor</span>
                <input value={row.value} onChange={(e) => changeExtraSpec(i, "value", e.target.value)} placeholder="MIL-STD-810H" />
              </label>
              <button type="button" className="btn btn--ghost" onClick={() => removeExtraSpec(i)}>
                Quitar
              </button>
            </div>
          ))}

          <button type="button" className="btn btn--ghost" onClick={addExtraSpec}>
            + Añadir especificación
          </button>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
          <button type="submit" className="btn btn--lg" disabled={saving}>
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear producto"}
          </button>
          <button type="button" className="btn btn--ghost btn--lg" onClick={() => navigate("/admin/productos")}>
            Cancelar
          </button>
        </div>
      </form>
    </>
  );
}
